import { maxLength, minValue, regex, required } from 'react-admin'

import { BrokerStatus } from '../components/Brokers/BrokerStatusTransitions'
import { DESTINATION, ORIGIN } from '../helpers/constants'

const _MAX_DOLLAR_INPUT_AMOUNT = 20000

export const validateTwoDecimals = (value: any) => {
  if (value && Number(value) !== parseFloat(Number(value).toFixed(2))) {
    return 'Must have a max of 2 decimals'
  }
  return undefined
}

// TODO: Create generic function to validate the number of decimals
export const validateThreeDecimals = (value: any) => {
  if (value && parseInt((value * 1000).toFixed(3)) % 1 !== 0) {
    return 'Must have a max of 3 decimals'
  }
  return undefined
}

export const validateInteger = (value: any) => {
  if (value && value % 1 !== 0) {
    return 'Must be an integer (without decimals)'
  }
  return undefined
}

export const validateNonZeroNumber = (value: any) => {
  if (value === 0) {
    return 'Must be a non-zero number'
  }
  return undefined
}

export const validatePositiveInteger = [validateInteger, minValue(0)]

export const validateStringIsNumberAtMostTwoDecimal = (value: any) => {
  if (value.includes(',')) {
    return 'Number cannot contain ",".'
  }
  if (!/^\d*(\.\d{1,2})?$/.test(value)) {
    return 'Must be a number with at most two decimals.'
  }
  return undefined
}

export const validateDollarAmount = (value: any) => {
  if (value >= _MAX_DOLLAR_INPUT_AMOUNT) {
    return 'Please enter an amount between $1.00 - $19,999.00'
  }
  return undefined
}

export const validateInvoiceItems = (values: any) => {
  const errors: any = {}
  const items = values.items
  if (!items || items.length === 0) {
    errors.items = 'Must include at least 1 invoice item.'
  }
  return errors
}

export const validateStatementItems = (values: any) => {
  const errors: any = {}
  if (
    (!values.line_items || values.line_items.length === 0) &&
    (!values.fee_items || values.fee_items.length === 0)
  ) {
    errors.line_items = 'Must include at least 1 line item or fee item.'
  }

  return errors
}

export const validateStatementTotalCap = (...props: any) => {
  const values = props[1]
  var subtotal = 0.0

  if (values.line_items?.length > 0) {
    values.line_items.forEach((line_item: any) => {
      if (line_item) {
        subtotal += parseFloat(line_item.amount)
      }
    })
  }
  if (values.fee_items?.length > 0) {
    values.fee_items.forEach((fee_item: any) => {
      if (fee_item) {
        subtotal -= parseFloat(fee_item.amount)
      }
    })
  }

  if (subtotal >= _MAX_DOLLAR_INPUT_AMOUNT) {
    return 'Statements total should be between $1.00 - $19,999.00'
  }
}

export const validateStatementAmounts: any = [validateTwoDecimals, required()]

export const validateStatementFeePercent: any = [validateTwoDecimals, required(), minValue(0)]

export const validateLoadStops = (values: any) => {
  /*
    This function expects 'values' to match the structure of a Load object.
    This validator ensures the load stops attached to a load are valid as a whole.
    Criteria: ORIGIN & DESTINATION stop types must both be present, all sequence numbers
    (ranging from 1...# of stops) must be present, and the ORIGIN & DESTINATION stops must
    be associated with the correct sequence numbers (1 for ORIGIN, # of stops for DESTINATION).
    */
  var errors: any = {}
  const loadStopErrorStr = "Missing 'ORIGIN' and/or 'DESTINATION' LoadStops"
  const loadStopSequenceErrorStr =
    "Sequence number mismatch. Make sure the 'ORIGIN' stop sequence number = 1, & the 'DESTINATION' sequence matches the total number of stops."

  if (!values.stops) {
    errors.load_stops = loadStopErrorStr
  } else {
    var sequenceNumbers = new Set(Array.from({ length: values.stops.length }, (_, i) => i + 1))
    let hasOrigin = false
    let hasDestination = false
    for (let i = 0; i < values.stops.length; i++) {
      let stop = values.stops[i]
      if (stop) {
        let sequenceNumber = values.stops[i].sequence_number
        if (typeof sequenceNumber === 'string') {
          sequenceNumber = parseInt(sequenceNumber)
        }
        if (sequenceNumbers.has(sequenceNumber)) {
          sequenceNumbers.delete(sequenceNumber)
        }

        if (stop.type === ORIGIN) {
          if (sequenceNumber !== 1) {
            errors.load_stops = loadStopSequenceErrorStr
            return errors
          }
          hasOrigin = true
        } else if (stop.type === DESTINATION) {
          if (sequenceNumber !== values.stops.length) {
            errors.load_stops = loadStopSequenceErrorStr
            return errors
          }
          hasDestination = true
        }
      }
    }
    if (!hasOrigin || !hasDestination) {
      errors.load_stops = loadStopErrorStr
      return errors
    }
    if (sequenceNumbers.size > 0) {
      errors.load_stops = `Missing stop sequence numbers: ${[...sequenceNumbers].join(', ')}`
      return errors
    }
  }
  return errors
}

export const isEligibleForVirtualCarrier = (broker: any, isPremium: boolean) => {
  const errors: any = {}
  if (!broker) {
    errors.load = { broker: 'There is no broker associated with this load. It cannot be approved.' }
    return errors
  }

  if (isPremium) {
    const isBrokerApproved = broker.status === BrokerStatus.Approved.id
    if (!isBrokerApproved && !broker.is_carrier_packet_complete) {
      errors.load = { broker: "The broker's status is not 'APPROVED' and the carrier packet is not setup." }
    } else if (!isBrokerApproved) {
      errors.load = { broker: "The broker's status is not 'APPROVED'" }
    } else if (!broker.is_carrier_packet_complete) {
      errors.load = { broker: "The broker's carrier packet is not setup." }
    }
  }

  return errors
}

export const futureDateValidator = (value: any) => {
  if (value) {
    const expirationDate = new Date(value)
    const currentDate = new Date()
    const maxDate = new Date(currentDate.getFullYear() + 20, currentDate.getMonth(), currentDate.getDate())
    if (expirationDate <= currentDate) {
      return 'Please select date in the future.'
    } else if (expirationDate >= maxDate) {
      return 'Expiration date can not be more than 20 years from today'
    }
  }
  return undefined
}

const pastDateValidator = (value: any) => {
  if (value && new Date(value) > new Date()) {
    return 'Please select date in the past.'
  }
  return undefined
}

export const validatePastDate = [pastDateValidator, required()]
export const validateFutureDate = [futureDateValidator, required()]
export const mobileValidator = regex(
  /^(\+1)?\d{9,15}$/,
  'Must be a valid phone number (optional: starting with +1)'
)
export const supportPhoneValidator = regex(
  /^(\+1)?\d{9,15}(,\d{1,4})?$/,
  'Must be a valid phone number (optional: starting with +1), and can contain a comma followed by an extension number'
)
export const validateFEINNumber = [required(), maxLength(9)]
export const validateMCNumber = [required(), maxLength(8)]

// eslint-disable-next-line no-useless-escape
export const validateNoSpecialChars = regex(/^[^\+\-\=\@]*$/, 'This field cannot contain special characters')

export const validateNoCharsOrSpecialChars = regex(
  /^[^+\-=@a-zA-Z]*$/,
  'Cannot contain special characters or letters'
)

export const validateVinCharacters = regex(/^[^IOQ]*$/, 'This field cannot contain the letters I, O, or Q')
